import {
    isResumableOnboardingStatus,
    type OnboardingApplicationStatus,
    OnboardingSubscriptionOrderState,
} from '@houzy/schemas/partner-cockpit/onboarding';
import {
    PARTNER_COCKPIT_PREFILL_EMAIL_PARAM,
    partnerCockpitUserSchema,
} from '@houzy/schemas/partner-cockpit/user-management';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, redirect } from '@tanstack/react-router';

import { RouteErrorFallback } from './components/router/RouteErrorFallback';
import { RouteNotFound } from './components/router/RouteNotFound';
import { RoutePending } from './components/router/RoutePending';
import {
    AcceptInvitationRoute,
    AcceptTermsRoute,
    CheckEmailPage,
    ConfirmEmailChangeError,
    ConfirmEmailChangePending,
    ConfirmEmailChangeSuccess,
    ForgotPasswordPage,
    ImpersonateError,
    ImpersonatePending,
    LoginPage,
    MagicLinkError,
    MagicLinkPending,
    ResetPasswordRoute,
} from './features/auth';
import { DEFAULT_NOTIFICATIONS_QUERY, notificationsQueryOptions, NotificationsPage } from './features/notifications';
import { NewOfferRequestsPage, OpenOfferRequestsPage } from './features/offerRequests';
import { prefetchOfferRequestDetailPage } from './features/offerRequests/api/prefetchOfferRequestDetailPage';
import {
    defaultOfferRequestsQuery,
    offerRequestsQueryOptions,
} from './features/offerRequests/api/useOfferRequestsList';
import { validateOfferRequestDetailSearch } from './features/offerRequests/detailSearch';
import { OfferRequestDetailRoute } from './features/offerRequests/pages/OfferRequestDetailRoute';
import {
    AccountStep,
    CompanyStep,
    isStepAheadOf,
    onboardingStepPath,
    type OnboardingStepId,
    type OnboardingStepPath,
    OnboardingLayout,
    OnboardingStartError,
    OnboardingStartPending,
    RegionsStep,
    RegistrationChannelStep,
    RegisterCompanyCheckEmailPage,
    RegisterCompanyPage,
    OnboardingCheckoutRoute,
    SubscriptionConfirmedStep,
    SubscriptionPlanStep,
    ValidationStep,
    WorkCategoriesStep,
} from './features/onboarding';
import { cantonsQueryOptions } from './features/onboarding/api/cantonsApi';
import { onboardingApplicationQueryOptions } from './features/onboarding/api/onboardingApi';
import { plansQueryOptions } from './features/onboarding/api/plansApi';
import { orderQueryOptions } from './features/onboarding/api/subscriptionApi';
import { workCategoriesQueryOptions } from './features/onboarding/api/workCategoriesApi';
import {
    DetailsStep,
    ReasonStep,
    REIMBURSEMENT_BASE_PATH,
    reimbursementEligibilityQueryOptions,
    ReimbursementLayout,
    TermsStep,
    toReimbursementOrigin,
    type ReimbursementOrigin,
} from './features/reimbursement';
import {
    AccountSettingsPage,
    cantonsQueryOptions as settingsCantonsQueryOptions,
    companyProfileQueryOptions,
    workCategorySelectionQueryOptions,
    workRegionSelectionQueryOptions,
    CompanySettingsPage,
    CreditsSettingsPage,
    MembersSettingsPage,
    NotificationsSettingsPage,
    partnerCriteriaQueryOptions,
    postalCodesByCantonQueryOptions,
    SETTINGS_BASE_PATH,
    SettingsLayout,
    settingsTabPath,
    SupportSettingsPage,
    teamMembersQueryOptions,
    VerificationSettingsPage,
    workCategoriesQueryOptions as settingsWorkCategoriesQueryOptions,
} from './features/settings';
import {
    subscriptionPlansQueryOptions,
    subscriptionQueryOptions,
    SubscriptionSettingsPage,
} from './features/subscription';
import { checkoutQueryOptions, SubscriptionCheckoutRoute } from './features/subscriptionCheckout';
import { validationQueryOptions } from './features/validation';
import { DEFAULT_LANGUAGE, i18n, isSupportedLanguage, type SupportedLanguage } from './i18n';
import { AppLayout } from './layouts/AppLayout';
import { CheckoutLayout } from './layouts/CheckoutLayout';
import { RootLayout } from './layouts/RootLayout';
import { SplitHeroLayout } from './layouts/SplitHeroLayout';
import { ApiError, apiRequest, invalidatePartnerCockpitCsrfToken } from './lib/api';
import {
    currentUserQuery,
    endServerSession,
    hasCachedCockpitUser,
    hasUsableCockpitSession,
    selectActiveMembership,
    selectHasActivePartner,
    selectIsPendingValidation,
    startPartnerCockpitSession,
    tearDownLocalSession,
    trackLogin,
    type PartnerCockpitUser,
} from './lib/auth';
import { partnerCockpitApiPath } from './lib/config';
import { toInternalLocation } from './lib/internalLocation';
import { takePendingRedirect } from './lib/pendingRedirect';
import { splitPrefillEmail } from './lib/prefillEmail';
import {
    isNoPartnerMembershipOrOnboardingApplicationError,
    queryClient,
    setSessionEndedHandler,
} from './lib/queryClient';
import { safeRedirect } from './lib/safeRedirect';
import { clearCockpitSessionHint } from './lib/sessionHint';
import { trackEvent } from './lib/trackingEvents';
import { isValidEmail } from './lib/validators';

export interface RouterContext {
    queryClient: QueryClient;
}

function getLocale(): SupportedLanguage {
    return isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

// OTP consume is single-use. beforeLoad can run twice (in dev StrictMode) and we reuse the same
// promise so we don't POST twice and briefly show an error.
const otpConsumptions = new Map<string, Promise<PartnerCockpitUser>>();
function consumeOtpOnce(otp: string, consume: () => Promise<PartnerCockpitUser>): Promise<PartnerCockpitUser> {
    const existing = otpConsumptions.get(otp);
    if (existing) {
        return existing;
    }
    const promise = consume();
    otpConsumptions.set(otp, promise);
    void promise.catch(() => {
        otpConsumptions.delete(otp);
    });
    return promise;
}

// Consuming any of these OTPs signs the caller in, so the switch goes through the same primitive as the login
// form rather than only writing the new user into the cache on top of the old one's.
async function consumeSessionOtp(client: QueryClient, otp: string, path: string): Promise<PartnerCockpitUser> {
    const user = await consumeOtpOnce(otp, () =>
        apiRequest(partnerCockpitApiPath(path), {
            method: 'POST',
            responseSchema: partnerCockpitUserSchema,
        }),
    );
    startPartnerCockpitSession(client, user);
    return user;
}

async function ensureSignedInUser(
    context: RouterContext,
    redirectTarget: string | undefined,
    { fresh = false, prefillEmail }: { fresh?: boolean; prefillEmail?: string } = {},
): Promise<PartnerCockpitUser> {
    try {
        return await (fresh
            ? context.queryClient.fetchQuery({ ...currentUserQuery, staleTime: 0 })
            : context.queryClient.ensureQueryData(currentUserQuery));
    } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            // Clear or the next cold load of /login redirects back here on a hint that outlived its session.
            clearCockpitSessionHint();
            throw redirect({ to: '/login', search: { redirect: redirectTarget, prefillEmail } });
        }
        throw error;
    }
}

async function resumableOnboarding(context: RouterContext): Promise<boolean> {
    try {
        const application = await context.queryClient.fetchQuery({
            ...onboardingApplicationQueryOptions,
            staleTime: 0,
        });
        return isResumableOnboardingStatus(application.status);
    } catch {
        return false;
    }
}

async function redirectIfSignedIn({ context }: { context: RouterContext }): Promise<void> {
    if (await hasUsableCockpitSession(context.queryClient)) {
        throw redirect({ to: '/' });
    }
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: RootLayout,
});

// Pathless layout: gates everything below it behind the current-user query
// (redirecting to /login on 401/403) and renders the AppLayout shell.
const authenticatedLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_authenticated',
    loader: ({ context }) => context.queryClient.ensureQueryData(validationQueryOptions),
    beforeLoad: async ({ context, location, cause }): Promise<void> => {
        const { target, prefillEmail, present } = splitPrefillEmail(location);
        // Entering the shell can follow a change the cached user predates, such as the onboarding submission that
        // makes a craftsman pending, and the gates below read that answer.
        const user = await ensureSignedInUser(context, target, { fresh: cause === 'enter', prefillEmail });
        // Signed in, so the login form never runs and the hint has nothing left to do.
        if (present) {
            throw redirect({ ...toInternalLocation(target), replace: true });
        }
        if (!user.tosAcceptedAt && !user.impersonation) {
            throw redirect({ to: '/accept-terms', search: { redirect: target } });
        }
        if (!selectHasActivePartner(user) && (await resumableOnboarding(context))) {
            throw redirect({ to: '/onboarding' });
        }
    },
    component: AppLayout,
});

function isPendingValidation(context: RouterContext): boolean {
    return selectIsPendingValidation(context.queryClient.getQueryData<PartnerCockpitUser>(currentUserQuery.queryKey));
}

function hasActivePartner(context: RouterContext): boolean {
    return selectHasActivePartner(context.queryClient.getQueryData<PartnerCockpitUser>(currentUserQuery.queryKey));
}

const PENDING_VALIDATION_LANDING = settingsTabPath('verification');

function redirectWhilePendingValidation({ context }: { context: RouterContext }): void {
    if (isPendingValidation(context)) {
        throw redirect({ to: PENDING_VALIDATION_LANDING });
    }
}

const indexRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/',
    beforeLoad: ({ context }): never => {
        throw redirect({ to: isPendingValidation(context) ? PENDING_VALIDATION_LANDING : '/new-requests' });
    },
});

const newOfferRequestsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/new-requests',
    staticData: { titleKey: 'OFFER_REQUESTS.NEW.TITLE' },
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context }) =>
        context.queryClient.ensureQueryData(offerRequestsQueryOptions(defaultOfferRequestsQuery('new'))),
    component: NewOfferRequestsPage,
});

const offerRequestDetailRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/new-requests/$offerRequestLeadUuid',
    staticData: { titleKey: 'OFFER_REQUESTS.DETAIL.SECTION' },
    validateSearch: validateOfferRequestDetailSearch,
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context, params }) => prefetchOfferRequestDetailPage(context.queryClient, params.offerRequestLeadUuid),
    component: OfferRequestDetailRoute,
});

const openOfferRequestsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/open-requests',
    staticData: { titleKey: 'OFFER_REQUESTS.OPEN.TITLE' },
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context }) =>
        context.queryClient.ensureQueryData(offerRequestsQueryOptions(defaultOfferRequestsQuery('open'))),
    component: OpenOfferRequestsPage,
});

const openOfferRequestDetailRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/open-requests/$offerRequestLeadUuid',
    staticData: { titleKey: 'OFFER_REQUESTS.DETAIL.SECTION' },
    validateSearch: validateOfferRequestDetailSearch,
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context, params }) => prefetchOfferRequestDetailPage(context.queryClient, params.offerRequestLeadUuid),
    component: OfferRequestDetailRoute,
});

const reimbursementLayoutRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: REIMBURSEMENT_BASE_PATH,
    staticData: { titleKey: 'REIMBURSEMENT.TITLE' },
    validateSearch: (search: Record<string, unknown>): { from: ReimbursementOrigin } => ({
        from: toReimbursementOrigin(search.from),
    }),
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context, params }) =>
        context.queryClient.ensureQueryData(reimbursementEligibilityQueryOptions(params.offerRequestLeadUuid)),
    component: ReimbursementLayout,
});

const reimbursementIndexRoute = createRoute({
    getParentRoute: () => reimbursementLayoutRoute,
    path: '/',
    beforeLoad: ({ params, search }): never => {
        throw redirect({ to: '/reimbursement/$offerRequestLeadUuid/terms', params, search, replace: true });
    },
});

const reimbursementTermsRoute = createRoute({
    getParentRoute: () => reimbursementLayoutRoute,
    path: 'terms',
    component: TermsStep,
});

const reimbursementReasonRoute = createRoute({
    getParentRoute: () => reimbursementLayoutRoute,
    path: 'reason',
    component: ReasonStep,
});

const reimbursementDetailsRoute = createRoute({
    getParentRoute: () => reimbursementLayoutRoute,
    path: 'details',
    component: DetailsStep,
});

const notificationsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/notifications',
    staticData: { titleKey: 'NOTIFICATIONS.TITLE' },
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context }) =>
        context.queryClient.ensureQueryData(notificationsQueryOptions(DEFAULT_NOTIFICATIONS_QUERY)),
    component: NotificationsPage,
});

// Existing partner-cockpit auth flow (email + password, magic-link, verify, reset).
const settingsLayoutRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: SETTINGS_BASE_PATH,
    staticData: { titleKey: 'SETTINGS.TITLE' },
    loader: ({ context }) =>
        Promise.all([
            context.queryClient.ensureQueryData(companyProfileQueryOptions),
            context.queryClient.ensureQueryData(workCategorySelectionQueryOptions),
            context.queryClient.ensureQueryData(workRegionSelectionQueryOptions),
            isPendingValidation(context) ? undefined : context.queryClient.ensureQueryData(subscriptionQueryOptions),
        ]),
    component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: '/',
    beforeLoad: () => {
        throw redirect({ to: settingsTabPath('company') });
    },
});

const settingsCompanyRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'company',
    staticData: { titleKey: 'SETTINGS.TABS.COMPANY' },
    loader: ({ context }) =>
        Promise.all([
            context.queryClient.ensureQueryData(settingsWorkCategoriesQueryOptions),
            context.queryClient.ensureQueryData(settingsCantonsQueryOptions),
            context.queryClient.ensureQueryData(postalCodesByCantonQueryOptions),
            hasActivePartner(context) ? context.queryClient.prefetchQuery(partnerCriteriaQueryOptions) : undefined,
        ]),
    component: CompanySettingsPage,
});

const settingsVerificationRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'verification',
    staticData: { titleKey: 'SETTINGS.TABS.VERIFICATION' },
    loader: ({ context }) => context.queryClient.ensureQueryData(validationQueryOptions),
    component: VerificationSettingsPage,
});

const settingsAccountRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'account',
    staticData: { titleKey: 'SETTINGS.TABS.ACCOUNT' },
    component: AccountSettingsPage,
});

const settingsMembersRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'members',
    staticData: { titleKey: 'SETTINGS.TABS.MEMBERS' },
    beforeLoad: redirectWhilePendingValidation,
    loader: ({ context }) => context.queryClient.ensureQueryData(teamMembersQueryOptions),
    component: MembersSettingsPage,
});

const settingsNotificationsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'notifications',
    staticData: { titleKey: 'SETTINGS.TABS.NOTIFICATIONS' },
    component: NotificationsSettingsPage,
});

const settingsSubscriptionRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'subscription',
    staticData: { titleKey: 'SETTINGS.TABS.SUBSCRIPTION' },
    validateSearch: (search: Record<string, unknown>): { returned?: 'billing' } => ({
        returned: search.returned === 'billing' ? 'billing' : undefined,
    }),
    beforeLoad: async ({ context }): Promise<void> => {
        redirectWhilePendingValidation({ context });
        const view = await context.queryClient.ensureQueryData(subscriptionQueryOptions);
        if (!view.subscription) {
            void context.queryClient.prefetchQuery(subscriptionPlansQueryOptions);
        }
    },
    component: SubscriptionSettingsPage,
});

const settingsCreditsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'credits',
    staticData: { titleKey: 'SETTINGS.TABS.CREDITS' },
    beforeLoad: ({ context }): void => {
        redirectWhilePendingValidation({ context });
        const user = context.queryClient.getQueryData<PartnerCockpitUser>(currentUserQuery.queryKey);
        if (selectActiveMembership(user)?.usesCredits !== true) {
            throw redirect({ to: settingsTabPath('company') });
        }
    },
    component: CreditsSettingsPage,
});

const settingsSupportRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: 'support',
    staticData: { titleKey: 'SETTINGS.TABS.SUPPORT' },
    component: SupportSettingsPage,
});

const settingsRoutes = settingsLayoutRoute.addChildren([
    settingsIndexRoute,
    settingsCompanyRoute,
    settingsVerificationRoute,
    settingsAccountRoute,
    settingsMembersRoute,
    settingsNotificationsRoute,
    settingsSubscriptionRoute,
    settingsCreditsRoute,
    settingsSupportRoute,
]);

const checkoutLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_checkout',
    component: CheckoutLayout,
});

const subscriptionCheckoutRoute = createRoute({
    getParentRoute: () => checkoutLayoutRoute,
    path: '/subscription-checkout/$token',
    staticData: { titleKey: 'CHECKOUT.HEADING' },
    validateSearch: validatePaymentSearch,
    loader: ({ context, params }) => context.queryClient.ensureQueryData(checkoutQueryOptions(params.token)),
    component: SubscriptionCheckoutRoute,
});

// Split-hero auth shell shared by /login, the /register-company craftsmen onboarding entry and
// every link-consuming page (invitation, password reset, magic link, email verification).
const splitHeroLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'split-hero-layout',
    component: SplitHeroLayout,
});

const loginRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/login',
    staticData: { titleKey: 'AUTH.LOGIN.TITLE' },
    validateSearch: validateLoginSearch,
    beforeLoad: async ({ context, search }): Promise<void> => {
        if (await hasUsableCockpitSession(context.queryClient)) {
            throw redirect(toInternalLocation(search.redirect));
        }
    },
    component: LoginPage,
});

const SIGN_OUT_REASON_NO_COCKPIT_ACCESS = 'no-cockpit-access';

function validateLoginSearch(search: Record<string, unknown>): {
    redirect?: string;
    prefillEmail?: string;
    reason?: typeof SIGN_OUT_REASON_NO_COCKPIT_ACCESS;
} {
    const candidate = search[PARTNER_COCKPIT_PREFILL_EMAIL_PARAM];
    return {
        ...validateRedirectSearch(search),
        prefillEmail: typeof candidate === 'string' && isValidEmail(candidate) ? candidate : undefined,
        reason: search.reason === SIGN_OUT_REASON_NO_COCKPIT_ACCESS ? SIGN_OUT_REASON_NO_COCKPIT_ACCESS : undefined,
    };
}

function validateEmailSearch(search: Record<string, unknown>): {
    email: string;
} {
    return { email: typeof search.email === 'string' ? search.email : '' };
}

function validateRedirectSearch(search: Record<string, unknown>): { redirect?: string } {
    return { redirect: typeof search.redirect === 'string' ? safeRedirect(search.redirect, '/') : undefined };
}

function validatePaymentSearch(search: Record<string, unknown>): { payment?: 'success' | 'cancelled' } {
    return { payment: search.payment === 'success' || search.payment === 'cancelled' ? search.payment : undefined };
}

const findAccountCheckEmailRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/find-account/check-email',
    staticData: { titleKey: 'AUTH.CHECK_EMAIL.LOGIN_LINK_SENT' },
    validateSearch: validateEmailSearch,
    beforeLoad: async ({ context, search }) => {
        await redirectIfSignedIn({ context });
        if (!search.email) {
            throw redirect({ to: '/login' });
        }
    },
    component: CheckEmailPage,
});

const registerCompanyRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/register-company',
    staticData: { titleKey: 'ONBOARDING.REGISTER.TITLE' },
    beforeLoad: async ({ context }): Promise<void> => {
        await redirectIfSignedIn({ context });
    },
    component: RegisterCompanyPage,
});

const registerCompanyCheckEmailRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/register-company/check-email',
    staticData: { titleKey: 'ONBOARDING.REGISTER.CHECK_EMAIL.TITLE' },
    validateSearch: validateEmailSearch,
    beforeLoad: async ({ context, search }) => {
        await redirectIfSignedIn({ context });
        if (!search.email) {
            throw redirect({ to: '/register-company' });
        }
    },
    component: RegisterCompanyCheckEmailPage,
});

const acceptTermsRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/accept-terms',
    staticData: { titleKey: 'AUTH.ACCEPT_TERMS.TITLE' },
    validateSearch: validateRedirectSearch,
    beforeLoad: async ({ context, search }): Promise<void> => {
        await ensureSignedInUser(context, search.redirect);
    },
    component: AcceptTermsRoute,
});

const forgotPasswordRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/forgot-password',
    staticData: { titleKey: 'AUTH.FORGOT_PASSWORD.TITLE' },
    beforeLoad: redirectIfSignedIn,
    component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/reset-password/$otp',
    staticData: { titleKey: 'AUTH.RESET_PASSWORD.TITLE' },
    component: ResetPasswordRoute,
});

const acceptInvitationRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/accept-invitation/$otp',
    staticData: { titleKey: 'ACCEPT_INVITATION.TITLE' },
    component: AcceptInvitationRoute,
});

const confirmEmailChangeRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/change-email/$otp',
    loader: async ({ params, context, abortController }): Promise<null> => {
        await apiRequest(partnerCockpitApiPath(`auth/email/change-confirm/${params.otp}`), {
            method: 'POST',
            signal: abortController.signal,
        });
        // The email change invalidates all sessions, which also drops the user the CSRF token is bound to.
        invalidatePartnerCockpitCsrfToken();
        context.queryClient.clear();
        return null;
    },
    component: ConfirmEmailChangeSuccess,
    pendingComponent: ConfirmEmailChangePending,
    errorComponent: ({ error }): ReturnType<typeof ConfirmEmailChangeError> => (
        <ConfirmEmailChangeError error={error} />
    ),
});

const magicLinkRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/magic-link/$otp',
    beforeLoad: async ({ params, context }): Promise<never> => {
        try {
            const user = await consumeSessionOtp(
                context.queryClient,
                params.otp,
                `auth/magic-link/consume/${params.otp}`,
            );
            trackLogin('magicLink', user);
        } catch (error) {
            // A link that has already been used, or has expired, is worth no error screen for someone who is
            // signed in anyway — where it was taking them is reachable either way. Any other failure still is.
            const spent = error instanceof ApiError && (error.status === 401 || error.status === 403);
            if (!spent || !(await hasUsableCockpitSession(context.queryClient))) {
                throw error;
            }
        }
        throw redirect(toInternalLocation(takePendingRedirect()));
    },
    pendingComponent: MagicLinkPending,
    errorComponent: ({ error }): ReturnType<typeof MagicLinkError> => <MagicLinkError error={error} />,
});

const impersonateRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/impersonate/$otp',
    beforeLoad: async ({ params, context }): Promise<never> => {
        await consumeSessionOtp(context.queryClient, params.otp, `auth/impersonate/consume/${params.otp}`);
        throw redirect({ to: '/' });
    },
    pendingComponent: ImpersonatePending,
    errorComponent: ({ error }): ReturnType<typeof ImpersonateError> => <ImpersonateError error={error} />,
});

const onboardingStartRoute = createRoute({
    getParentRoute: () => splitHeroLayoutRoute,
    path: '/onboarding/start/$otp',
    beforeLoad: async ({ params, context }): Promise<never> => {
        const user = await consumeSessionOtp(context.queryClient, params.otp, `onboarding/start/${params.otp}`);
        if (selectHasActivePartner(user)) {
            throw redirect({ to: '/' });
        }
        trackEvent('Onboarding Started', {});
        throw redirect({ to: '/onboarding' });
    },
    pendingComponent: OnboardingStartPending,
    errorComponent: ({ error }): ReturnType<typeof OnboardingStartError> => <OnboardingStartError error={error} />,
});

const onboardingLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/onboarding',
    beforeLoad: async ({ context }): Promise<void> => {
        try {
            await context.queryClient.ensureQueryData(currentUserQuery);
        } catch (error) {
            if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                throw redirect({ to: '/register-company' });
            }
            throw error;
        }

        let status: OnboardingApplicationStatus;
        try {
            ({ status } = await context.queryClient.ensureQueryData(onboardingApplicationQueryOptions));
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.errorCode === 'ONBOARDING_APPLICATION_NOT_IN_PROGRESS') {
                    throw redirect({ to: '/' });
                }
                // Signed in but no onboarding yet, or the session is gone → request a link.
                if (error.status === 401 || error.status === 403) {
                    throw redirect({ to: '/register-company' });
                }
            }
            throw error;
        }
        if (!isResumableOnboardingStatus(status)) {
            throw redirect({ to: '/' });
        }
    },
    component: OnboardingLayout,
});

async function currentOnboardingStepPath(context: RouterContext): Promise<OnboardingStepPath> {
    const application = await context.queryClient.ensureQueryData(onboardingApplicationQueryOptions);
    return onboardingStepPath(application.currentStep);
}

const onboardingIndexRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/',
    beforeLoad: async ({ context }): Promise<never> => {
        throw redirect({ to: await currentOnboardingStepPath(context) });
    },
});

function redirectIfStepIsAhead(step: OnboardingStepId) {
    return async ({ context }: { context: RouterContext }): Promise<void> => {
        const application = await context.queryClient.ensureQueryData(onboardingApplicationQueryOptions);
        if (isStepAheadOf(step, application.currentStep)) {
            throw redirect({ to: onboardingStepPath(application.currentStep) });
        }
    };
}

const workCategoriesStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/work-categories',
    beforeLoad: redirectIfStepIsAhead('work-categories'),
    staticData: { titleKey: 'ONBOARDING.STEPS.WORK_CATEGORIES' },
    loader: ({ context }) => context.queryClient.ensureQueryData(workCategoriesQueryOptions),
    component: WorkCategoriesStep,
});

const regionsStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/regions',
    beforeLoad: redirectIfStepIsAhead('regions'),
    staticData: { titleKey: 'ONBOARDING.STEPS.REGIONS' },
    loader: ({ context }) => context.queryClient.ensureQueryData(cantonsQueryOptions),
    component: RegionsStep,
});

const companyStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/company',
    beforeLoad: redirectIfStepIsAhead('company'),
    staticData: { titleKey: 'ONBOARDING.STEPS.COMPANY' },
    component: CompanyStep,
});

const accountStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/account',
    beforeLoad: redirectIfStepIsAhead('account'),
    staticData: { titleKey: 'ONBOARDING.STEPS.ACCOUNT' },
    component: AccountStep,
});

const validationStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/validation',
    beforeLoad: redirectIfStepIsAhead('validation'),
    staticData: { titleKey: 'ONBOARDING.STEPS.VALIDATION' },
    component: ValidationStep,
});

const registrationChannelStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/registration-channel',
    beforeLoad: redirectIfStepIsAhead('registration-channel'),
    staticData: { titleKey: 'ONBOARDING.REGISTRATION_CHANNEL.TITLE' },
    component: RegistrationChannelStep,
});

const subscriptionPlanStepRoute = createRoute({
    getParentRoute: () => onboardingLayoutRoute,
    path: '/subscription',
    beforeLoad: redirectIfStepIsAhead('subscription'),
    staticData: { titleKey: 'ONBOARDING.STEPS.SUBSCRIPTION' },
    loader: ({ context }) => context.queryClient.ensureQueryData(plansQueryOptions),
    component: SubscriptionPlanStep,
});

const onboardingCheckoutLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_onboarding-checkout',
    beforeLoad: async ({ context }): Promise<void> => {
        try {
            await context.queryClient.ensureQueryData(currentUserQuery);
        } catch (error) {
            if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                throw redirect({ to: '/register-company' });
            }
            throw error;
        }
    },
    component: CheckoutLayout,
});

const onboardingCheckoutRoute = createRoute({
    getParentRoute: () => onboardingCheckoutLayoutRoute,
    path: '/onboarding/subscription/checkout',
    staticData: { titleKey: 'CHECKOUT.HEADING' },
    validateSearch: validatePaymentSearch,
    loader: ({ context }) => context.queryClient.ensureQueryData(orderQueryOptions()),
    component: OnboardingCheckoutRoute,
});

const onboardingConfirmedRoute = createRoute({
    getParentRoute: () => onboardingCheckoutLayoutRoute,
    path: '/onboarding/subscription/confirmed',
    staticData: { titleKey: 'ONBOARDING.SUBSCRIPTION.CONFIRMED.TITLE' },
    // A terminal screen, so reaching it on a bookmark or a refresh means the sale is not finished yet. We can send the
    // partner to the step that was not completed.
    beforeLoad: async ({ context }): Promise<void> => {
        const order = await context.queryClient.ensureQueryData(orderQueryOptions());
        if (order.state === OnboardingSubscriptionOrderState.AwaitingPlanSelection) {
            throw redirect({ to: '/onboarding/subscription' });
        }
        if (order.state !== OnboardingSubscriptionOrderState.Confirmed) {
            throw redirect({ to: '/onboarding/subscription/checkout' });
        }
    },
    loader: ({ context }) => context.queryClient.ensureQueryData(orderQueryOptions()),
    component: SubscriptionConfirmedStep,
});

const routeTree = rootRoute.addChildren([
    authenticatedLayoutRoute.addChildren([
        indexRoute,
        newOfferRequestsRoute,
        offerRequestDetailRoute,
        openOfferRequestsRoute,
        openOfferRequestDetailRoute,
        reimbursementLayoutRoute.addChildren([
            reimbursementIndexRoute,
            reimbursementTermsRoute,
            reimbursementReasonRoute,
            reimbursementDetailsRoute,
        ]),
        notificationsRoute,
        settingsRoutes,
    ]),
    splitHeroLayoutRoute.addChildren([
        loginRoute,
        findAccountCheckEmailRoute,
        registerCompanyRoute,
        registerCompanyCheckEmailRoute,
        acceptTermsRoute,
        forgotPasswordRoute,
        resetPasswordRoute,
        acceptInvitationRoute,
        confirmEmailChangeRoute,
        magicLinkRoute,
        impersonateRoute,
        onboardingStartRoute,
    ]),
    checkoutLayoutRoute.addChildren([subscriptionCheckoutRoute]),
    onboardingCheckoutLayoutRoute.addChildren([onboardingCheckoutRoute, onboardingConfirmedRoute]),
    onboardingLayoutRoute.addChildren([
        onboardingIndexRoute,
        workCategoriesStepRoute,
        regionsStepRoute,
        accountStepRoute,
        companyStepRoute,
        validationStepRoute,
        registrationChannelStepRoute,
        subscriptionPlanStepRoute,
    ]),
]);

export const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    // Let React Query's `staleTime` decide cache freshness, not the router's loader cache.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // The onboarding steps scroll inside their own <main>, not the window, so it has to be reset on navigation too.
    scrollToTopSelectors: ['window', 'main'],
    context: {
        queryClient,
    },
    rewrite: {
        input: ({ url }) => {
            const segments = url.pathname.split('/').filter(Boolean);
            const firstSegment = segments[0];
            if (firstSegment && isSupportedLanguage(firstSegment)) {
                // On first load i18n isn't ready yet, but LanguageDetector reads the path
                // anyway. After that, sync it on back/forward and link clicks.
                if (i18n.isInitialized && i18n.language !== firstSegment) {
                    void i18n.changeLanguage(firstSegment);
                }
                const rest = segments.slice(1).join('/');
                url.pathname = rest ? `/${rest}` : '/';
            }
            return url;
        },
        output: ({ url }) => {
            const locale = getLocale();
            url.pathname = `/${locale}${url.pathname === '/' ? '' : url.pathname}`;
            return url;
        },
    },
    defaultPendingComponent: RoutePending,
    defaultErrorComponent: RouteErrorFallback,
    defaultNotFoundComponent: RouteNotFound,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
    interface StaticDataRouteOption {
        titleKey?: string;
    }
}

// A partner-cockpit session can be invalidated server-side while the user is still on a page. When a request surfaces that,
// drop what it cached and send the user to login rather than leaving stale data on screen until the next navigation.
setSessionEndedHandler((error) => {
    // Nothing to end, so leave the navigation to the route guard — which carries the search this would drop.
    if (!hasCachedCockpitUser(queryClient)) {
        return;
    }
    const noPartnerMembershipOrOnboardingApplication = isNoPartnerMembershipOrOnboardingApplicationError(error);
    // The whole cache: whichever path starts the teardown is the only one that runs, so logout returning early
    // must not leave the previous session's data — an impersonated partner's included — behind.
    const tornDown = tearDownLocalSession(queryClient, () =>
        router.navigate({
            to: '/login',
            search: noPartnerMembershipOrOnboardingApplication ? { reason: SIGN_OUT_REASON_NO_COCKPIT_ACCESS } : {},
        }),
    );
    if (tornDown && noPartnerMembershipOrOnboardingApplication) {
        endServerSession();
    }
});
